// External imports
import React, { useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Dialog,
  Fade,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Internal imports
import useApi from "../../hooks/useApi";
import MovieCard from "../../components/MovieCard";
import TVShowCard from "../../components/TVShowCard";
import BookCard from "../../components/BookCard";

import {
  applyStatusClearToCache,
  applyStatusWriteToCache,
} from "../../utils/cacheUtils";
import { toast } from "react-toastify";
import { getContentLabel } from "../../utils/utils";
import { useContentStatus, useEntry } from "../../hooks/useCustomQuery";
import { useAuth } from "../../context/authContext";
import ConfirmActionDialog from "../../components/ConfirmActionDialog";
import ContentJournal from "../../components/ContentJournal";
import ContentFriends from "../../components/ContentFriends";
import FocusedEntry from "./FocusedEntry";

const tabs = [
  { value: "you", label: "You", enabled: true },
  { value: "following", label: "Following", enabled: true },
  // { value: "community", label: "Community", enabled: false },
];

// Mobile: vertical travel (px) at which a swipe collapses (up) or expands
// (down) the details card. Small enough to feel immediate, large enough that a
// tap's drift never trips it.
const SWIPE_DISTANCE = 10;
// Height (px) of the fade at the bottom of the tab panel that signals more
// content below — iOS only shows scrollbars mid-scroll, and the panel is
// locked while the details card is expanded, so nothing else would.
const SCROLL_FADE_HEIGHT = 32;

function LibraryModal({
  open,
  contentType,
  contentId,
  entryId = null,
  onClearEntry,
  onContentChange,
  onClose,
}) {
  const { editStatus, deleteStatus, addStatus } = useApi();
  const theme = useTheme();
  const { user, openAuthDialog } = useAuth();
  const queryClient = useQueryClient();
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  // seriesWide comes from the backend (delete_status detects it from the
  // content id alone), so the dialog can pick the right warning wording.
  const [confirmDelete, setConfirmDelete] = useState({
    contentId: null,
    seriesWide: false,
  });
  const [tabValue, setTabValue] = useState("you");
  // Bumped to signal TVShowCard's status dropdown to open, anchored to its own
  // status button — lets ContentJournal's empty-state link open it despite
  // living in a sibling component. A counter (not a boolean) so repeat clicks
  // while already open still re-fire the effect that reads it.
  const [statusMenuOpenSignal, setStatusMenuOpenSignal] = useState(0);
  const handleRequestStatusMenu = () =>
    setStatusMenuOpenSignal((count) => count + 1);

  // Fullscreen (mobile) has two states: the details card expanded with the
  // tab panel locked, or collapsed with the tab panel scrollable. They're
  // switched by deliberate gestures, never by scroll position — deriving the
  // state from scrollTop let the collapse animation and momentum scrolling
  // fight each other. A swipe up collapses; a fresh pull down from the top of
  // the panel, or a tap on the collapsed card, expands.
  const scrollRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  // Lags `collapsed` by the collapse animation, so a second swipe made
  // mid-animation can't start scrolling the panel while the layout still moves.
  const [scrollUnlocked, setScrollUnlocked] = useState(false);
  const swipeStartRef = useRef(null);

  // Desktop has no collapsed state, and new content (e.g. a season tile in the
  // Following tab) always opens expanded.
  useEffect(() => {
    setCollapsed(false);
  }, [fullscreen, contentId]);

  useEffect(() => {
    if (!collapsed) {
      setScrollUnlocked(false);
      return;
    }
    const timer = setTimeout(
      () => setScrollUnlocked(true),
      theme.transitions.duration.standard, // MUI Collapse's default timeout
    );
    return () => clearTimeout(timer);
  }, [collapsed, theme]);

  const panelScrollable = !fullscreen || scrollUnlocked;

  const expandCard = () => {
    setCollapsed(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  // Eligibility is decided once, at touchstart. Collapsed, only a touch that
  // begins with the panel settled at the top may expand — so neither the
  // scroll that brought it back up (that finger has lifted) nor a touch during
  // iOS's top bounce (scrollTop is negative then) nor one mid-collapse can.
  const handleSwipeStart = (event) => {
    if (collapsed && !(scrollUnlocked && scrollRef.current?.scrollTop === 0)) {
      swipeStartRef.current = null;
      return;
    }
    const touch = event.touches[0];
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  };
  // Only a mostly-vertical swipe counts, so horizontal swipes (e.g. the season
  // tabs) and taps pass through untouched. Nothing here calls preventDefault:
  // expanded, native panning is ruled out by touch-action; collapsed, the
  // panel's top bounce is ruled out by overscroll-behavior.
  const handleSwipeMove = (event) => {
    const start = swipeStartRef.current;
    if (!start) return;
    const touch = event.touches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dy) <= Math.abs(dx)) return;
    if (!collapsed && -dy >= SWIPE_DISTANCE) {
      swipeStartRef.current = null;
      setCollapsed(true);
    } else if (collapsed && dy >= SWIPE_DISTANCE) {
      swipeStartRef.current = null;
      expandCard();
    }
  };

  // The panel's content wrapper, as state so the fade effect below re-runs
  // once it mounts (it waits on the linked entry resolving).
  const [panelContentEl, setPanelContentEl] = useState(null);
  // Fades the panel's bottom edge while there's more below, shrinking the fade
  // with the distance left so it eases out on reaching the end. Written to the
  // DOM directly rather than via state, so scrolling never re-renders the
  // modal. Purely visual, so this listener can't feed back into layout.
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl || !panelContentEl) return;
    const updateFade = () => {
      const remaining =
        scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
      const fade = Math.max(0, Math.min(SCROLL_FADE_HEIGHT, remaining));
      const mask =
        fade > 0
          ? `linear-gradient(to bottom, #000 calc(100% - ${fade}px), transparent)`
          : "none";
      scrollEl.style.maskImage = mask;
      scrollEl.style.webkitMaskImage = mask;
    };
    updateFade();
    scrollEl.addEventListener("scroll", updateFade, { passive: true });
    // The panel resizes as the card collapses/expands; the content as entries
    // load, change or switch tab.
    const resizeObserver = new ResizeObserver(updateFade);
    resizeObserver.observe(scrollEl);
    resizeObserver.observe(panelContentEl);
    return () => {
      scrollEl.removeEventListener("scroll", updateFade);
      resizeObserver.disconnect();
    };
  }, [panelContentEl]);

  // Any tap on the collapsed card expands it, bar its own buttons (close).
  const handleCollapsedCardClick = (event) => {
    if (event.target.closest("button")) return;
    expandCard();
  };

  // ?entry=<id> resolves into one of two views, decided by whose entry it is
  // rather than where the link came from:
  //  - someone else's → focusedEntry: an @username tab of its own, selected on
  //    arrival and kept until the modal closes
  //  - the viewer's own → scopedEntryId: the You tab, narrowed to that entry
  // Either only applies while the entry belongs to the active content. One
  // that fails to resolve (e.g. deleted) just leaves the normal tabs.
  const { data: linkedEntry, isLoading: linkedEntryLoading } =
    useEntry(entryId);
  const linkedEntryIsActive = linkedEntry?.entry.content_id === contentId;
  const focusedEntry =
    linkedEntryIsActive && !linkedEntry.is_own ? linkedEntry : null;
  const scopedEntryId =
    linkedEntryIsActive && linkedEntry.is_own ? entryId : null;

  // Ownership isn't known until the entry resolves, so its tab is selected
  // then rather than as the initial tab value.
  const focusedEntryId = focusedEntry?.entry.journal_id ?? null;
  useEffect(() => {
    if (focusedEntryId) setTabValue("entry");
  }, [focusedEntryId]);

  // Falls back to You once the entry tab is gone (e.g. a season switch dropped
  // ?entry=), so Tabs is never given a value it has no tab for.
  const activeTab = tabValue === "entry" && !focusedEntry ? "you" : tabValue;

  // The @username tab's close button: drops ?entry= to remove the tab, landing
  // on You if it was the one being viewed. Propagation is stopped on press as
  // well as click, so the tab underneath neither ripples nor gets selected.
  const handleCloseEntryTab = (event) => {
    event.stopPropagation();
    if (tabValue === "entry") setTabValue("you");
    onClearEntry?.();
  };

  // Every status write lands on the You tab, where the entry it creates sits
  // at the top waiting to be filled in. A scope on the viewer's own entry is
  // cleared too, or it would hide that new entry — someone else's entry tab
  // stays, as it hides nothing.
  const handleStatusWritten = () => {
    setTabValue("you");
    if (scopedEntryId) onClearEntry?.();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const { data: statusFromQuery } = useContentStatus(contentId, {
    enabled: !!user,
  });

  const status = statusFromQuery ?? "none";

  const handleCloseModal = () => {
    onClose?.();
  };

  const editStatusMutation = useMutation({
    mutationFn: ({ contentId, status: newStatus }) =>
      editStatus({ contentId, status: newStatus }),
    onSuccess: (data, variables) => {
      handleStatusWritten();
      applyStatusWriteToCache({
        queryClient,
        data,
        requestedContentId: variables.contentId,
        oldStatus: variables.oldStatus,
        contentType,
        username: user.username,
      });
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: ({ contentId, confirm }) => deleteStatus(contentId, confirm),
    onSuccess: (data, variables) => {
      if (data.requireConfirmation) {
        setConfirmDelete({
          contentId: variables.contentId,
          seriesWide: !!data.seriesWide,
        });
        setOpenConfirmDelete(true);
      } else {
        setOpenConfirmDelete(false);
        // A scoped entry of the viewer's own went with the status; someone
        // else's entry tab is unaffected and stays.
        if (scopedEntryId) onClearEntry?.();
        applyStatusClearToCache({
          queryClient,
          data,
          requestedContentId: variables.contentId,
          contentType,
          username: user.username,
        });
        toast.success(data.message);
      }
    },
  });

  const addStatusMutation = useMutation({
    mutationFn: addStatus,
    onSuccess: (data, variables) => {
      handleStatusWritten();
      applyStatusWriteToCache({
        queryClient,
        data,
        requestedContentId: variables.contentId,
        oldStatus: null,
        contentType,
        username: user.username,
      });
    },
  });

  // contentId/currentStatus default to the active tab's content, but TV series
  // commands (e.g. "Finish S2") target a different season, so callers can
  // override both to act on that season instead. Whether a delete cascades
  // across a whole series (and therefore always needs confirming) is decided
  // by the backend from the content id alone — see deleteStatusMutation.
  const handleStatusChange = (
    newStatus,
    { contentId: targetContentId = contentId, currentStatus = status } = {},
  ) => {
    // This function uses mutateAsync to pass the outcome of the status change to the caller
    // The caller is responsible for defining conditional actions based on mutation and for
    // cleaning up the promise via .catch()
    if (!user) {
      openAuthDialog("Tracking content");
      return Promise.resolve();
    }
    if (newStatus === "logAgain") {
      return addStatusMutation.mutateAsync({
        contentId: targetContentId,
        status: "finished",
        createJournalEntry: true,
      });
    } else if (newStatus === "none") {
      return deleteStatusMutation.mutateAsync({
        contentId: targetContentId,
        confirm: false,
      });
    } else if (currentStatus === "none" && newStatus) {
      return addStatusMutation.mutateAsync({
        contentId: targetContentId,
        status: newStatus,
        createJournalEntry: true,
      });
    } else {
      return editStatusMutation.mutateAsync({
        contentId: targetContentId,
        status: newStatus,
        oldStatus: currentStatus,
      });
    }
  };

  return (
    <>
      <Dialog
        fullScreen={fullscreen}
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="library-card-dialog"
        fullWidth
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            // Deliberately no safe-area padding here. Unlike every other dialog, this
            // one's Paper IS the scrim (translucent black — see PaperProps), so padding
            // the container would shorten it and leave the status-bar/home-indicator
            // strips dimmed only by the backdrop under viewport-fit=cover. The insets go
            // on the content column inside the Paper instead.
            paddingTop: fullscreen ? 0 : "10vh",
          },
        }}
        PaperProps={{
          sx: {
            maxWidth: 700,
            boxShadow: "none",
            backgroundImage: "none", // needed to remove default overlay MUI adds in dark mode to desaturate colour
            backgroundColor: fullscreen
              ? alpha(theme.palette.common.black, 0.5)
              : "transparent",
          },
        }}
      >
        {/* Both layouts are a column bounded in height: the full 100dvh on
            mobile, the Paper's viewport-based max-height on desktop. Every
            level down to the tab panel can shrink (minHeight: 0), so the
            details card and tab row keep their natural height and only the
            tab panel scrolls once the content outgrows that bound. */}
        <Box
          onClick={onClose}
          sx={{
            // 100% of the (full-bleed) Paper rather than 100dvh, so this column can
            // never outgrow the scrim it sits on.
            height: fullscreen ? "100%" : "auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            // Keeps the content clear of the status bar and home indicator while the
            // scrim above still reaches the viewport edges. 0px outside the
            // installed-PWA/notched case.
            ...(fullscreen && {
              pt: "var(--safe-top)",
              pb: "var(--safe-bottom)",
            }),
          }}
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            // Swipes anywhere on the details or tabs card collapse/expand it.
            onTouchStart={fullscreen ? handleSwipeStart : undefined}
            onTouchMove={fullscreen ? handleSwipeMove : undefined}
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              // Mobile fills the screen so the spacer at the bottom can take
              // up any backdrop left under a short tabs card.
              height: fullscreen ? "100%" : undefined,
              // Until the panel unlocks, the browser may only pan sideways, so
              // a vertical swipe can never become a native scroll. iOS reads
              // this at touchstart, so the swipe that collapses the card can't
              // carry on into a scroll — that takes a fresh touch.
              touchAction: panelScrollable ? undefined : "pan-x",
            }}
          >
            <Box
              onClick={collapsed ? handleCollapsedCardClick : undefined}
              sx={{
                mt: fullscreen ? 2 : 0,
                mx: fullscreen ? 2 : 0,
                flexShrink: 0,
                position: "relative",
              }}
            >
              {contentType === "movie" ? (
                <MovieCard
                  contentType={contentType}
                  contentId={contentId}
                  onClose={handleCloseModal}
                  status={status}
                  onStatusChange={handleStatusChange}
                  fullscreen={fullscreen}
                  statusMenuOpenSignal={statusMenuOpenSignal}
                  collapsed={collapsed}
                />
              ) : contentType === "tvshow" ? (
                <TVShowCard
                  contentType={contentType}
                  contentId={contentId}
                  onContentChange={onContentChange}
                  onClose={handleCloseModal}
                  status={status}
                  onStatusChange={handleStatusChange}
                  fullscreen={fullscreen}
                  statusMenuOpenSignal={statusMenuOpenSignal}
                  collapsed={collapsed}
                />
              ) : contentType === "book" ? (
                <BookCard
                  contentType={contentType}
                  contentId={contentId}
                  onClose={handleCloseModal}
                  status={status}
                  onStatusChange={handleStatusChange}
                  fullscreen={fullscreen}
                  statusMenuOpenSignal={statusMenuOpenSignal}
                  collapsed={collapsed}
                />
              ) : null}
              {/* A hint that the collapsed card expands; the tap target is
                  the whole card, so it ignores pointer events itself. Sized
                  and inset to mirror the cards' close icon in the top right
                  (24px glyph, 20px from the edges). */}
              <Fade in={collapsed}>
                <ExpandMoreIcon
                  sx={{
                    position: "absolute",
                    right: 20,
                    bottom: 20,
                    color: "action.active", // IconButton's default, as on close
                    pointerEvents: "none",
                  }}
                />
              </Fade>
            </Box>

            <Box
              sx={{
                mt: 2,
                pb: fullscreen ? 4 : 0,
                mx: fullscreen ? 2 : 0,
                minHeight: 0,
                // Sizes to its content, shrinking to whatever height the
                // (flexShrink: 0) details card above leaves so the tab panel
                // inside scrolls.
                flex: "0 1 auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto",
                  minHeight: 0,
                }}
              >
                <CardContent
                  sx={{
                    padding: 2,
                    "&:last-child": {
                      paddingBottom: 2,
                    },
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 auto",
                    minHeight: 0,
                    // The Tabs row stays put; only the panel below it (the
                    // scroll Box further down) scrolls.
                    overflow: "hidden",
                  }}
                >
                  {linkedEntryLoading ? (
                    // Which tabs to show, and which is selected, depends on
                    // who owns the linked entry, so hold off until that's known.
                    <Stack direction="column" spacing={1}>
                      <Skeleton width="70%" />
                      <Skeleton width="60%" />
                    </Stack>
                  ) : (
                    <>
                      <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                          maxWidth: "100%",
                          borderBottom: 1,
                          borderColor: "divider",
                          minHeight: 30,
                          // No bottom margin: each tab panel opens with its
                          // own mt: 2, and margins don't collapse in this flex
                          // column, so a margin here would add to that gap.
                          flexShrink: 0,
                        }}
                      >
                        {/* Someone else's entry leads the row, labelled with its
                            author. Truncated so a long username can't push
                            You/Following off a phone screen, and never
                            case-transformed, since it's an identifier. */}
                        {focusedEntry && (
                          <Tab
                            value="entry"
                            label={
                              <Box
                                component="span"
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: "block",
                                    maxWidth: { xs: 120, sm: 200 },
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  @{focusedEntry.author.username}
                                </Box>
                                {/* A span, not an IconButton — Tab is itself a
                                    button, and buttons can't nest. */}
                                <Box
                                  component="span"
                                  role="button"
                                  aria-label="Close this entry"
                                  onClick={handleCloseEntryTab}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  sx={{
                                    display: "flex",
                                    ml: 0.5,
                                    p: 0.25,
                                    borderRadius: "50%",
                                    color: "text.secondary",
                                    "&:hover": { bgcolor: "action.hover" },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </Box>
                              </Box>
                            }
                            sx={{
                              minWidth: 20,
                              py: 0,
                              minHeight: 30,
                              textTransform: "none",
                            }}
                          />
                        )}
                        {tabs.map((tab) => (
                          <Tab
                            key={tab.value}
                            value={tab.value}
                            label={tab.label}
                            disabled={!tab.enabled}
                            sx={{
                              minWidth: 20,
                              py: 0,
                              minHeight: 30,
                            }}
                          />
                        ))}
                      </Tabs>

                      {/* The scroll container, on both layouts. On mobile it
                          stays locked while the details card is expanded. */}
                      <Box
                        ref={scrollRef}
                        sx={{
                          flex: "1 1 auto",
                          minHeight: 0,
                          overflowY: panelScrollable ? "auto" : "hidden",
                          // No iOS rubber-band: a pull down from the top
                          // expands the card instead, and a bounce running
                          // alongside that expansion would visibly fight it.
                          overscrollBehaviorY: fullscreen ? "none" : undefined,
                        }}
                      >
                        {/* Wrapped only so its size can be observed for the
                            fade. */}
                        <Box ref={setPanelContentEl}>
                          {activeTab === "entry" ? (
                            <FocusedEntry linkedEntry={focusedEntry} />
                          ) : activeTab === "you" ? (
                            <ContentJournal
                              fullscreen={fullscreen}
                              contentType={contentType}
                              scopedEntryId={scopedEntryId}
                              activeContentId={contentId}
                              status={status}
                              onExitScope={onClearEntry}
                              onStatusChange={handleStatusChange}
                              onContentChange={onContentChange}
                              onRequestStatusMenu={handleRequestStatusMenu}
                            />
                          ) : (
                            <ContentFriends
                              contentId={contentId}
                              onContentChange={onContentChange}
                            />
                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Fullscreen: this column fills the screen, so the backdrop left
                below a short tabs card sits inside it, where taps are stopped.
                This spacer takes that space and closes on tap instead. Its
                zero basis means it only ever takes what the tabs card leaves. */}
            {fullscreen && (
              <Box onClick={handleCloseModal} sx={{ flex: "1 1 0" }} />
            )}
          </Box>
        </Box>
      </Dialog>

      <ConfirmActionDialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        title={
          confirmDelete.seriesWide
            ? "Clear tracking for this series?"
            : "Delete journal entries?"
        }
        message={
          <Typography>
            {confirmDelete.seriesWide ? (
              "This removes tracking and journal entries for the whole series, including every season."
            ) : (
              <>
                Removing tracking for this{" "}
                {getContentLabel({ type: contentType, lowercase: true })} will
                also delete your journal entries for it.
              </>
            )}
          </Typography>
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => {
          deleteStatusMutation.mutate({
            contentId: confirmDelete.contentId ?? contentId,
            confirm: true,
          });
        }}
      />
    </>
  );
}

export default LibraryModal;
